/**
 * <LockedFeaturePanel /> — full-panel replacement rendered inside screen
 * bodies (e.g. Portfolio Insights section, Net Worth card) instead of the
 * real feature, when the current user is on the Continuity System
 * gateway tier.
 *
 * Purpose: even if a locked feature is embedded deep inside another tab
 * (not a top-level nav item), the user still sees WHY it's locked and how
 * to unlock — same upgrade modal, single source of truth.
 *
 * For any other tier: renders children untouched (zero visual/behaviour cost).
 *
 * Usage:
 *   <LockedFeaturePanel featureLabel="Portfolio Insights">
 *     <ActualInsightsWidget ... />
 *   </LockedFeaturePanel>
 */
import React from 'react';
import { Lock, ArrowRight } from 'lucide-react';
import { useUserTier } from '../../hooks/useUserTier';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';

export function LockedFeaturePanel({ featureLabel, description, children, className = '' }) {
  const { isGatewayContinuity, loading } = useUserTier();
  const { openUpgradeModal } = useUpgradeModal();

  if (loading || !isGatewayContinuity) return children;

  const testId = `locked-panel-${(featureLabel || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <div
      className={`rounded-xl border border-slate-200 bg-linear-to-br from-slate-50 to-white p-6 sm:p-8 flex flex-col items-start gap-3 ${className}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-slate-500">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100">
          <Lock className="w-4 h-4" />
        </span>
        <span className="text-xs font-medium uppercase tracking-wide">Portfolio Clarity</span>
      </div>
      <h3 className="text-lg sm:text-xl font-serif font-semibold text-slate-900">
        {featureLabel}
      </h3>
      {description && (
        <p className="text-sm text-slate-600 max-w-md">{description}</p>
      )}
      <button
        type="button"
        onClick={() => openUpgradeModal(featureLabel)}
        className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline"
        data-testid={`${testId}-cta`}
      >
        See how to unlock <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default LockedFeaturePanel;
